import { BaseSqliteRepository } from './base-sqlite.repository';
import { SqliteQueryError } from '../../errors';
import type { IIncomeRepository } from '@app/domain/ports';
import type { Income, IncomeListProjection } from '@app/domain/entities';
import type { IncomeFilter, IncomeCursor, TransferCandidateQuery } from '@app/domain/ports';

interface IncomeRow {
  id: number;
  date: number; // Unix timestamp in seconds
  accountId: number;
  accountName: string;
  amount: number;
  currencyCode: string;
  categoryId: number;
  categoryName: string;
  parentCategoryId?: number | null;
  parentCategoryName?: string | null;
  rate: number;
  note: string;
}

export class IncomeRepository extends BaseSqliteRepository implements IIncomeRepository {
  private readonly BASE_FROM = `
    FROM incomes e
    INNER JOIN accounts a ON e.account_id = a._id
    INNER JOIN categories cat ON e.category_id = cat._id
    LEFT JOIN categories parent_cat ON cat.parent_category_id = parent_cat._id
    INNER JOIN currencies c ON e.currency_id = c._id
    INNER JOIN available_currencies AS cl ON c.available_currency_id = cl._id
    WHERE e.is_deleted = 0
  `;

  private readonly BASE_FIELDS = [
    'e._id as id',
    'e.income_date as date',
    'e.account_id as accountId',
    'a.name as accountName',
    'e.category_id as categoryId',
    'cat.name as categoryName',
    'cat.parent_category_id as parentCategoryId',
    'parent_cat.name as parentCategoryName',
    'e.amount as amount',
    'cl.ticker as currencyCode',
    'e.exchange_rate as rate',
    'e.note as note',
  ];

  /**
   * Minimal FROM for aggregate queries (COUNT / SUM).
   * Avoids the 4-way JOIN used by BASE_FROM — with large tables the joins
   * cause thousands of random B-tree lookups that dominate OPFS I/O.
   * Filters that need join columns (currencyCode, categoryId) are rewritten
   * in applyFilterForAggregates() to use a subquery instead.
   */
  private readonly AGGREGATE_FROM = `FROM incomes e WHERE e.is_deleted = 0`;

  async count(filter?: IncomeFilter): Promise<number> {
    const sql = `SELECT COUNT(*) AS total ${this.AGGREGATE_FROM}`;
    const { query, params } = this.applyFilterForAggregates(sql, filter);
    const result = await this.queryOne<{ total: number }>(query, params);
    return result?.total ?? 0;
  }

  async aggregateTotalAmount(filter: IncomeFilter): Promise<number> {
    const sql = `SELECT SUM(e.amount) AS total ${this.AGGREGATE_FROM}`;
    const { query, params } = this.applyFilterForAggregates(sql, filter);
    const result = await this.queryOne<{ total: number }>(query, params);
    return result?.total ?? 0;
  }

  async findAll(cursor: IncomeCursor, filter?: IncomeFilter): Promise<IncomeListProjection[]> {
    const sql = `
      SELECT
        ${this.BASE_FIELDS.join(',\n ')},
        CASE WHEN LAG(e.income_date) OVER (ORDER BY e.income_date DESC, e._id DESC) != e.income_date THEN 1 ELSE 0 END as isFirstInDate
      ${this.BASE_FROM}
    `;

    // eslint-disable-next-line prefer-const
    let { query, params } = this.applyFilter(sql, filter);

    query += ` ORDER BY e.income_date DESC, e._id DESC LIMIT ? OFFSET ?`;
    params.push(cursor.limit, cursor.offset ?? 0);

    const rows = await this.query<IncomeRow & { isFirstInDate: number }>(query, params);
    return rows.map((row) =>
      this.mapRowToEntity(row, (r, m) => {
        m.isFirstInDate = r.isFirstInDate === 1;
        m.parentCategoryId = r.parentCategoryId || undefined;
        m.parentCategoryName = r.parentCategoryName || undefined;
      }),
    );
  }

  async getById(id: number): Promise<Income | null> {
    const query = `
      SELECT
      ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
       AND e._id = ?`;
    const row = await this.queryOne<IncomeRow>(query, [id]);
    return row ? this.mapRowToEntity(row) : null;
  }

  async getByIdIncludingDeleted(id: number): Promise<Income | null> {
    const query = `
      SELECT
      ${this.BASE_FIELDS.join(',\n ')}
      FROM incomes e
      INNER JOIN accounts a ON e.account_id = a._id
      INNER JOIN categories cat ON e.category_id = cat._id
      LEFT JOIN categories parent_cat ON cat.parent_category_id = parent_cat._id
      INNER JOIN currencies c ON e.currency_id = c._id
      INNER JOIN available_currencies AS cl ON c.available_currency_id = cl._id
      WHERE e._id = ?`;
    const row = await this.queryOne<IncomeRow>(query, [id]);
    return row ? this.mapRowToEntity(row) : null;
  }

  async softDeleteKeepingMetadata(id: number): Promise<void> {
    await this.execute("UPDATE incomes SET is_deleted = 1, updated_at = strftime('%s','now') WHERE _id = ?", [id]);
  }

  async restore(id: number): Promise<void> {
    await this.execute("UPDATE incomes SET is_deleted = 0, updated_at = strftime('%s','now') WHERE _id = ?", [id]);
  }

  async findTransferCandidates(query: TransferCandidateQuery): Promise<Income[]> {
    const dateFrom = new Date(query.dateFrom).setUTCHours(0, 0, 0, 0) / 1000;
    const dateTo = new Date(query.dateTo).setUTCHours(0, 0, 0, 0) / 1000;
    const anchor = new Date(query.anchorDate).setUTCHours(0, 0, 0, 0) / 1000;

    const sql = `
      SELECT
      ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
        AND e.amount = ?
        AND cl.ticker = ?
        AND e.account_id <> ?
        AND e.income_date BETWEEN ? AND ?
        AND NOT EXISTS (
          SELECT 1 FROM account_transfers t
          WHERE t.source_income_id = e._id AND t.is_deleted = 0
        )
      ORDER BY ABS(e.income_date - ?) ASC, e.income_date DESC, e._id DESC`;

    const rows = await this.query<IncomeRow>(sql, [
      query.amount,
      query.currencyCode,
      query.excludeAccountId,
      dateFrom,
      dateTo,
      anchor,
    ]);
    return rows.map((row) => this.mapRowToEntity(row));
  }

  async insert(income: Omit<Income, 'id' | 'accountName' | 'categoryName'>): Promise<number> {
    const date = new Date(income.date).setUTCHours(0, 0, 0, 0) / 1000;

    const result = await this.queryOne<{ id: number }>(
      `INSERT INTO incomes (income_date, account_id, amount, currency_id, category_id, exchange_rate, note, note_normalized)
       VALUES (?, ?, ?, (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?), ?, ?, ?, ?)
       RETURNING _id AS id`,
      [
        date,
        income.accountId,
        income.amount,
        income.currencyCode,
        income.categoryId,
        income.rate,
        income.note,
        income.note?.toLowerCase() ?? '',
      ],
    );

    if (!result?.id) {
      throw new SqliteQueryError('INSERT INTO incomes', [], 'Failed to insert income');
    }

    return result.id;
  }

  async update(income: Omit<Income, 'accountName' | 'categoryName'>): Promise<void> {
    const date = new Date(income.date).setUTCHours(0, 0, 0, 0) / 1000;

    await this.execute(
      `UPDATE incomes
       SET income_date = ?,
           account_id = ?,
           amount = ?,
           currency_id = (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?),
           category_id = ?,
           exchange_rate = ?,
           note = ?,
           note_normalized = ?,
           updated_at = strftime('%s','now')
       WHERE _id = ?`,
      [
        date,
        income.accountId,
        income.amount,
        income.currencyCode,
        income.categoryId,
        income.rate,
        income.note,
        income.note?.toLowerCase() ?? '',
        income.id,
      ],
    );
  }

  async delete(id: number): Promise<void> {
    await this.execute("UPDATE incomes SET is_deleted = 1, updated_at = strftime('%s','now') WHERE _id = ?", [id]);
  }

  async countByCategory(categoryId: number): Promise<number> {
    const query = `
      SELECT COUNT(*) AS total
      FROM incomes e
      WHERE e.category_id = ? AND e.is_deleted = 0
    `;
    const result = await this.queryOne<{ total: number }>(query, [categoryId]);
    return result?.total ?? 0;
  }

  async bulkUpdateCategory(sourceCategoryId: number, targetCategoryId: number): Promise<void> {
    await this.execute(
      `UPDATE incomes
       SET category_id = ?,
           updated_at = strftime('%s','now')
       WHERE category_id = ? AND is_deleted = 0`,
      [targetCategoryId, sourceCategoryId],
    );
  }

  async bulkSetCategory(ids: number[], categoryId: number): Promise<void> {
    if (ids.length === 0) return;
    const placeholders = ids.map(() => '?').join(', ');
    await this.execute(
      `UPDATE incomes
       SET category_id = ?,
           updated_at = strftime('%s','now')
       WHERE _id IN (${placeholders}) AND is_deleted = 0`,
      [categoryId, ...ids],
    );
  }

  async countByAccount(accountId: number): Promise<number> {
    const query = `
      SELECT COUNT(*) AS total
      FROM incomes e
      WHERE e.account_id = ? AND e.is_deleted = 0
    `;
    const result = await this.queryOne<{ total: number }>(query, [accountId]);
    return result?.total ?? 0;
  }

  async bulkUpdateAccount(sourceAccountId: number, targetAccountId: number): Promise<void> {
    await this.execute(
      `UPDATE incomes
       SET account_id = ?,
           updated_at = strftime('%s','now')
       WHERE account_id = ? AND is_deleted = 0`,
      [targetAccountId, sourceAccountId],
    );
  }

  async findRecentCategoryIdsByNote(params: { note: string; limit: number }): Promise<number[]> {
    const normalizedNote = params.note.trim().toLowerCase();
    if (!normalizedNote || params.limit <= 0) {
      return [];
    }

    const rows = await this.query<{ categoryId: number }>(
      `SELECT e.category_id as categoryId
       FROM incomes e
       WHERE e.is_deleted = 0
         AND e.note_normalized = ?
       ORDER BY e.income_date DESC, e._id DESC
       LIMIT ?`,
      [normalizedNote, params.limit],
    );

    return rows.map((row) => row.categoryId);
  }

  async findSimilarTransaction(params: {
    date: Date;
    accountId: number;
    amount: number;
    currencyCode: string;
    amountTolerance: number;
    externalSystem: string;
  }): Promise<number | null> {
    const normalizedDate = new Date(params.date).setUTCHours(0, 0, 0, 0) / 1000;
    const minAmount = params.amount - params.amountTolerance;
    const maxAmount = params.amount + params.amountTolerance;

    const query = `
      SELECT e._id as id
      FROM incomes e
      INNER JOIN accounts a ON e.account_id = a._id
      INNER JOIN currencies c ON e.currency_id = c._id
      INNER JOIN available_currencies cl ON c.available_currency_id = cl._id
      WHERE e.is_deleted = 0
        AND e.account_id = ?
        AND cl.ticker = ?
        AND e.income_date = ?
        AND e.amount >= ?
        AND e.amount <= ?
        AND NOT EXISTS (
          SELECT 1 FROM external_transactions et
          WHERE et.income_id = e._id
            AND et.source = ?
            AND et.is_deleted = 0
        )
      ORDER BY ABS(e.amount - ?) ASC
      LIMIT 1
    `;

    const row = await this.queryOne<{ id: number }>(query, [
      params.accountId,
      params.currencyCode,
      normalizedDate,
      minAmount,
      maxAmount,
      params.externalSystem,
      params.amount,
    ]);

    return row?.id ?? null;
  }

  async findCandidatesForImport(params: {
    externalSystem: string;
    accountIds: number[];
    dateFrom: Date;
    dateTo: Date;
  }): Promise<Array<{ id: number; date: number; accountId: number; currencyCode: string; amount: number }>> {
    if (params.accountIds.length === 0) {
      return [];
    }

    const dateFrom = new Date(params.dateFrom).setUTCHours(0, 0, 0, 0) / 1000;
    const dateTo = new Date(params.dateTo).setUTCHours(0, 0, 0, 0) / 1000;
    const placeholders = params.accountIds.map(() => '?').join(', ');

    const query = `
      SELECT e._id as id,
             e.income_date as date,
             e.account_id as accountId,
             cl.ticker as currencyCode,
             e.amount as amount
      FROM incomes e
      INNER JOIN currencies c ON e.currency_id = c._id
      INNER JOIN available_currencies cl ON c.available_currency_id = cl._id
      WHERE e.is_deleted = 0
        AND e.account_id IN (${placeholders})
        AND e.income_date >= ?
        AND e.income_date <= ?
        AND NOT EXISTS (
          SELECT 1 FROM external_transactions et
          WHERE et.income_id = e._id
            AND et.source = ?
            AND et.is_deleted = 0
        )
    `;

    return await this.query<{ id: number; date: number; accountId: number; currencyCode: string; amount: number }>(
      query,
      [...params.accountIds, dateFrom, dateTo, params.externalSystem],
    );
  }

  /**
   * Deduplicates and validates a list of category IDs, removing non-positive or
   * non-integer values.
   */
  private toValidCategoryIds(ids: number[] | undefined): number[] {
    return Array.from(new Set(ids ?? [])).filter((id) => Number.isInteger(id) && id > 0);
  }

  /**
   * Applies filter predicates to a query that uses the full BASE_FROM join.
   * Category columns are accessible via the `cat` alias.
   */
  private applyFilter(
    query: string,
    filter?: IncomeFilter,
    params: unknown[] = [],
  ): { query: string; params: unknown[] } {
    if (filter?.accountId) {
      query += ` AND e.account_id = ?`;
      params.push(filter.accountId);
    }

    if (filter?.categoryId) {
      query += ' AND (cat._id = ? OR cat.parent_category_id = ?)';
      params.push(filter.categoryId);
      params.push(filter.categoryId);
    }

    const includeCategoryIds = this.toValidCategoryIds(filter?.includeCategoryIds);
    if (includeCategoryIds.length > 0) {
      const placeholders = includeCategoryIds.map(() => '?').join(', ');
      query += ` AND (
        cat._id IN (${placeholders})
        OR cat.parent_category_id IN (${placeholders})
      )`;
      params.push(...includeCategoryIds, ...includeCategoryIds);
    }

    const excludeCategoryIds = this.toValidCategoryIds(filter?.excludeCategoryIds);
    if (excludeCategoryIds.length > 0) {
      const placeholders = excludeCategoryIds.map(() => '?').join(', ');
      query += ` AND NOT (
        cat._id IN (${placeholders})
        OR cat.parent_category_id IN (${placeholders})
      )`;
      params.push(...excludeCategoryIds, ...excludeCategoryIds);
    }

    if (filter?.currencyCode) {
      query += ` AND cl.ticker = ?`;
      params.push(filter.currencyCode);
    }

    if (filter?.dateFrom) {
      query += ` AND e.income_date >= ?`;
      params.push(new Date(filter.dateFrom).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.dateTo) {
      query += ` AND e.income_date < ?`;
      params.push(new Date(filter.dateTo).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.amountFrom != null) {
      query += ` AND e.amount >= ?`;
      params.push(filter.amountFrom);
    }

    if (filter?.amountTo != null) {
      query += ` AND e.amount <= ?`;
      params.push(filter.amountTo);
    }

    if (filter?.note) {
      query += ` AND e.note_normalized LIKE ?`;
      params.push(`%${filter.note.toLowerCase()}%`);
    }

    return { query, params };
  }

  /**
   * Like applyFilter but for aggregate-only queries (COUNT / SUM) that do not
   * join accounts / categories / currencies.
   *
   * - categoryId / includeCategoryIds / excludeCategoryIds → use `e.category_id`
   *   with inline subqueries instead of the `cat` alias (no JOIN available here)
   * - currencyCode → resolved via a scalar subquery instead of a JOIN
   * - all other filter fields reference only the `incomes e` table
   */
  private applyFilterForAggregates(
    query: string,
    filter?: IncomeFilter,
    params: unknown[] = [],
  ): { query: string; params: unknown[] } {
    if (filter?.accountId) {
      query += ` AND e.account_id = ?`;
      params.push(filter.accountId);
    }

    if (filter?.categoryId) {
      query += ` AND e.category_id IN (
        SELECT ? AS cat_id
        UNION ALL
        SELECT _id FROM categories WHERE parent_category_id = ? AND is_deleted = 0
      )`;
      params.push(filter.categoryId);
      params.push(filter.categoryId);
    }

    const includeCategoryIds = this.toValidCategoryIds(filter?.includeCategoryIds);
    if (includeCategoryIds.length > 0) {
      const placeholders = includeCategoryIds.map(() => '?').join(', ');
      query += ` AND (
        e.category_id IN (${placeholders})
        OR e.category_id IN (SELECT _id FROM categories WHERE parent_category_id IN (${placeholders}) AND is_deleted = 0)
      )`;
      params.push(...includeCategoryIds, ...includeCategoryIds);
    }

    const excludeCategoryIds = this.toValidCategoryIds(filter?.excludeCategoryIds);
    if (excludeCategoryIds.length > 0) {
      const placeholders = excludeCategoryIds.map(() => '?').join(', ');
      query += ` AND NOT (
        e.category_id IN (${placeholders})
        OR e.category_id IN (SELECT _id FROM categories WHERE parent_category_id IN (${placeholders}) AND is_deleted = 0)
      )`;
      params.push(...excludeCategoryIds, ...excludeCategoryIds);
    }

    if (filter?.currencyCode) {
      query += ` AND e.currency_id = (
        SELECT c._id FROM currencies c
        INNER JOIN available_currencies cl ON c.available_currency_id = cl._id
        WHERE cl.ticker = ?
      )`;
      params.push(filter.currencyCode);
    }

    if (filter?.dateFrom) {
      query += ` AND e.income_date >= ?`;
      params.push(new Date(filter.dateFrom).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.dateTo) {
      query += ` AND e.income_date < ?`;
      params.push(new Date(filter.dateTo).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.amountFrom != null) {
      query += ` AND e.amount >= ?`;
      params.push(filter.amountFrom);
    }

    if (filter?.amountTo != null) {
      query += ` AND e.amount <= ?`;
      params.push(filter.amountTo);
    }

    if (filter?.note) {
      query += ` AND e.note_normalized LIKE ?`;
      params.push(`%${filter.note.toLowerCase()}%`);
    }

    return { query, params };
  }

  private mapRowToEntity<T extends IncomeRow, P extends Income>(row: T, configure?: (row: T, model: P) => void): P {
    const model = {
      id: row.id,
      date: new Date(row.date * 1000),
      accountId: row.accountId,
      accountName: row.accountName,
      categoryId: row.categoryId,
      categoryName: row.categoryName,
      amount: row.amount,
      currencyCode: row.currencyCode,
      rate: !row.rate ? 1 : row.rate,
      note: row.note,
    } as P;

    configure?.(row, model);
    return model;
  }
}
